import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Home, ShieldCheck, KeyRound, Mail, AlertTriangle, X, Plus, Paperclip, Upload, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";
const POSSESSO_OPZIONI = [
  { value: "Prima casa - detraz. 50%", detr: "50" },
  { value: "Seconda casa - detraz. 36%", detr: "36" },
];

export default function RacProprietaPublic() {
  const { token } = useParams();
  const [step, setStep] = useState("loading"); // loading | error | identify | otp | form | done
  // Iter122i · scroll a inizio pagina ad ogni cambio di step (mobile critico su form pubblico).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);
  const [status, setStatus] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [cf4, setCf4] = useState("");
  const [otp, setOtp] = useState("");
  const [emailMask, setEmailMask] = useState("");
  const [session, setSession] = useState("");
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [atto, setAtto] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [fieldErr, setFieldErr] = useState("");
  const [titolari, setTitolari] = useState([]);
  const [dirittiReali, setDirittiReali] = useState([]);
  const [openTit, setOpenTit] = useState(-1);
  const [docs, setDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docTipo, setDocTipo] = useState("identita");
  const [docErr, setDocErr] = useState("");

  const addTitolare = () => { setOpenTit(titolari.length); setTitolari((a) => [...a, { nome: "", codice_fiscale: "", diritto_reale: "", quota_diritto: "", tipo_possesso: "", percentuale_detrazione: "", num_occupanti: "" }]); };
  const removeTitolare = (i) => { setTitolari((a) => a.filter((_, j) => j !== i)); setOpenTit(-1); };
  const setTit = (i, key, v) => setTitolari((a) => a.map((t, j) => j === i ? { ...t, [key]: v, ...(key === "tipo_possesso" ? { percentuale_detrazione: (POSSESSO_OPZIONI.find((o) => o.value === v)?.detr) || t.percentuale_detrazione } : {}) } : t));

  useEffect(() => {
    axios.get(`${API}/rac-proprieta/${token}`)
      .then((r) => {
        setStatus(r.data);
        if (r.data.completed) { setStep("done"); setDone({ ok: true, message: "I dati per questa unità risultano già inviati. Grazie." }); }
        else if (r.data.expired || !r.data.valid) { setStep("error"); setErrMsg("Il link è scaduto o non è più valido. Contatti lo Studio per riceverne uno nuovo."); }
        else { setStep("identify"); }
      })
      .catch((e) => { setStep("error"); setErrMsg(e.response?.data?.detail || "Link non valido o non più attivo."); });
  }, [token]);

  const requestOtp = async () => {
    setBusy(true); setFieldErr("");
    try {
      const { data: r } = await axios.post(`${API}/rac-proprieta/${token}/request-otp`, { cf_last4: cf4 });
      setEmailMask(r.email_mask || "");
      setStep("otp");
    } catch (e) { setFieldErr(e.response?.data?.detail || "Verifica non riuscita."); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true); setFieldErr("");
    try {
      const { data: r } = await axios.post(`${API}/rac-proprieta/${token}/verify-otp`, { otp });
      setSession(r.session_token);
      setData(r);
      setDirittiReali(r.diritti_reali || []);
      setTitolari(r.altri_titolari || []);
      setDocs(Array.isArray(r.documenti) ? r.documenti : []);
      const init = {};
      (r.campi || []).forEach((c) => { init[c.key] = c.value || ""; });
      setVals(init);
      setStep("form");
    } catch (e) { setFieldErr(e.response?.data?.detail || "Codice non valido."); }
    finally { setBusy(false); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setDocErr("");
    setUploadingDoc(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: r } = await axios.post(
        `${API}/rac-proprieta/${token}/documento?session_token=${encodeURIComponent(session)}&tipo=${docTipo}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setDocs(r.documenti || []);
    } catch (e) { setDocErr(e.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setUploadingDoc(false); }
  };

  const removeDoc = async (fid) => {
    if (!window.confirm("Rimuovere questo documento?")) return;
    setDocErr("");
    try {
      const { data: r } = await axios.delete(
        `${API}/rac-proprieta/${token}/documento/${fid}?session_token=${encodeURIComponent(session)}`
      );
      setDocs(r.documenti || []);
    } catch (e) { setDocErr(e.response?.data?.detail || "Rimozione non riuscita"); }
  };

  const setV = (key, v) => setVals((s) => {
    const next = { ...s, [key]: v };
    if (key === "tipo_casa" && data?.detrazione_default?.[v] && !s.percentuale_detrazione) next.percentuale_detrazione = data.detrazione_default[v];
    return next;
  });

  const invia = async () => {
    setBusy(true);
    try {
      const payload = { ...vals, altri_titolari: titolari, atto_notorio: atto, privacy_consenso: privacy, session_token: session };
      const { data: res } = await axios.post(`${API}/rac-proprieta/${token}`, payload);
      setDone(res); setStep("done");
    } catch (e) { setDone({ ok: false, message: e.response?.data?.detail || "Invio non riuscito. Riprova." }); setStep("done"); }
    finally { setBusy(false); }
  };

  const field = (c) => {
    const common = "w-full h-12 border-2 border-slate-300 rounded-lg px-3";
    if (c.tipo === "numero") return <input data-testid={`rac-campo-${c.key}`} type="number" className={common} value={vals[c.key] || ""} onChange={(e) => setV(c.key, e.target.value)} />;
    if (c.tipo === "data") return <input data-testid={`rac-campo-${c.key}`} type="date" className={common} value={vals[c.key] || ""} onChange={(e) => setV(c.key, e.target.value)} />;
    if (c.tipo === "scelta") return (
      <select data-testid={`rac-campo-${c.key}`} className={common + " bg-white"} value={vals[c.key] || ""} onChange={(e) => setV(c.key, e.target.value)}>
        <option value="">— Seleziona —</option>
        {(c.opzioni || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
    return <input data-testid={`rac-campo-${c.key}`} type="text" className={common} value={vals[c.key] || ""} onChange={(e) => setV(c.key, e.target.value)} />;
  };

  const header = (
    <div className="flex items-center gap-3 mb-6">
      <img src={EMBLEM} alt="Ginius" className="w-12 h-12 rounded-xl ring-2 ring-[#D4AF5A]/70" />
      <div className="leading-none">
        <div className="font-brand font-bold text-3xl text-brand" style={{ WebkitTextStroke: "0.5px #fff" }}>Ginius<span className="text-[#D4AF5A]">.</span></div>
        {status?.titolare?.ragione_sociale && <div className="text-xs text-[#E7C9A0]" data-testid="public-header-studio">{status.titolare.ragione_sociale}</div>}
      </div>
    </div>
  );

  const luogo = status ? `${status.condominio_nome}${status.unita ? ` · unità ${status.unita}` : ""}` : "";

  return (
    <div className="min-h-screen bg-[#1A1818] flex flex-col items-center px-4 py-8">
      {header}
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6" data-testid="rac-public-card">
        {step === "loading" && <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>}

        {step === "error" && (
          <div className="text-center py-8" data-testid="rac-error">
            <AlertTriangle className="w-14 h-14 mx-auto mb-3 text-amber-500" />
            <p className="text-red-700 font-semibold">{errMsg}</p>
          </div>
        )}

        {step === "identify" && (
          <div data-testid="rac-step-identify">
            <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1"><ShieldCheck className="w-6 h-6" /> Verifica della Sua identità</div>
            <p className="text-sm text-slate-600 mb-1">{luogo}</p>
            <p className="text-sm text-slate-600 mb-4">Per proteggere i Suoi dati (GDPR), prima di accedere al modulo è necessaria una verifica. {status?.cf_required ? "Inserisca le ultime 4 cifre del Suo codice fiscale: Le invieremo poi un codice di verifica via email." : "Le invieremo un codice di verifica alla Sua email registrata."}</p>
            {status?.cf_required && (
              <div className="mb-4">
                <label className="block text-sm font-bold text-slate-700 mb-1">Ultime 4 cifre/caratteri del codice fiscale</label>
                <input data-testid="rac-cf4" type="text" maxLength={4} value={cf4} onChange={(e) => setCf4(e.target.value.toUpperCase())}
                  className="w-40 h-12 border-2 border-slate-300 rounded-lg px-3 tracking-widest text-center text-lg font-mono" placeholder="••••" />
              </div>
            )}
            {fieldErr && <p className="text-sm text-red-700 mb-3" data-testid="rac-verify-error">{fieldErr}</p>}
            <button data-testid="rac-request-otp-btn" disabled={busy || (status?.cf_required && cf4.trim().length < 4)} onClick={requestOtp}
              className="w-full h-13 min-h-[3rem] rounded-xl bg-brand hover:bg-brand-dark text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              <Mail className="w-5 h-5" /> {busy ? "Invio…" : "Invia codice di verifica"}
            </button>
          </div>
        )}

        {step === "otp" && (
          <div data-testid="rac-step-otp">
            <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1"><KeyRound className="w-6 h-6" /> Inserisci il codice ricevuto</div>
            <p className="text-sm text-slate-600 mb-4">Abbiamo inviato un codice di verifica a 5 cifre all'indirizzo <b>{emailMask}</b>. Lo inserisca qui sotto (valido 10 minuti).</p>
            <input data-testid="rac-otp" type="text" inputMode="numeric" maxLength={5} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="w-48 h-14 border-2 border-slate-300 rounded-lg px-3 tracking-[0.5em] text-center text-2xl font-mono mb-3" placeholder="•••••" />
            {fieldErr && <p className="text-sm text-red-700 mb-3" data-testid="rac-otp-error">{fieldErr}</p>}
            <button data-testid="rac-verify-otp-btn" disabled={busy || otp.length < 5} onClick={verifyOtp}
              className="w-full h-13 min-h-[3rem] rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              <ShieldCheck className="w-5 h-5" /> {busy ? "Verifica…" : "Verifica e prosegui"}
            </button>
            <button data-testid="rac-resend-otp" onClick={requestOtp} disabled={busy} className="w-full mt-3 text-sm text-brand underline">Non hai ricevuto il codice? Invia di nuovo</button>
          </div>
        )}

        {step === "form" && data && (
          <div data-testid="rac-step-form">
            <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1"><Home className="w-6 h-6" /> Registro Anagrafe Condominiale (RAC)</div>
            <p className="text-sm text-slate-600 mb-1">{luogo}</p>
            {data.nome && <p className="text-sm text-slate-500 mb-3">Intestatario: <b>{data.nome}</b></p>}
            <p className="text-sm text-slate-600 mb-4">Compili i dati richiesti per completare l'anagrafe del Suo immobile. I dati verranno trasmessi allo Studio e aggiornati automaticamente.</p>

            <div className="space-y-4">
              {(data.campi || []).map((c) => (
                <div key={c.key}>
                  <label className="block text-sm font-bold text-slate-700 mb-1">{c.label} {c.obbligatorio && <span className="text-red-600">*</span>}</label>
                  {field(c)}
                </div>
              ))}
            </div>

            {/* Riepilogo unità immobiliari collegate al soggetto — solo consultazione */}
            {(data.unita_multiple || []).length > 0 && (
              <div className="mt-5 border-2 border-slate-200 rounded-lg overflow-hidden" data-testid="rac-unita-panel">
                <div className="bg-slate-50 px-3 py-2 border-b">
                  <div className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Home className="w-4 h-4 text-brand" />
                    Unità immobiliari intestate a Lei ({(data.unita_multiple || []).length})
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Se un'unità non risulta corretta o mancante, contatti lo Studio. La compilazione dei dati (quote, detrazioni, occupanti) si intende riferita a tutte le unità qui elencate.
                  </div>
                </div>
                <div className="divide-y">
                  {(data.unita_multiple || []).map((u, i) => (
                    <div key={i} className="p-3 text-sm" data-testid={`rac-unita-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-xs font-bold shrink-0">{i + 1}</span>
                        <span className="font-semibold">
                          {u.interno ? `Int. ${u.interno}` : (u.sub ? `Sub ${u.sub}` : "Unità senza interno")}
                          {u.tipo_unita ? ` · ${u.tipo_unita}` : ""}
                          {u.palazzina ? ` · Pal. ${u.palazzina}` : ""}
                          {u.piano ? ` · Piano ${u.piano}` : ""}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 pl-8">
                        Foglio {u.foglio || "—"} · Mappale {u.mappale_particella || "—"} · Sub {u.sub || "—"}
                        {u.sezione_urbana ? ` · Sez. urb. ${u.sezione_urbana}` : ""}
                        {u.comune_catastale ? ` · Comune cat. ${u.comune_catastale}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!data.is_conduttore && (
              <div className="mt-5" data-testid="altri-titolari-section">
                {(() => {
                  const totale = (Number(vals.quota_proprieta) || 0) + titolari.reduce((s, t) => s + (Number(t.quota_diritto) || 0), 0);
                  const ok = Math.abs(totale - 100) < 0.01;
                  const cls = ok ? "bg-green-100 text-green-800 border-green-300" : (totale > 100 ? "bg-red-100 text-red-800 border-red-300" : "bg-amber-100 text-amber-800 border-amber-300");
                  const msg = ok ? "✓" : (totale > 100 ? "(supera 100%)" : "(non chiude 100%)");
                  return (
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <label className="block text-sm font-bold text-slate-700">Altri soggetti titolari di diritti</label>
                      <span data-testid="titolari-totale" className={`text-xs font-bold px-2 py-1 rounded border ${cls}`}
                        title="Somma della Sua quota + quote degli altri titolari">
                        Titolari totali: {totale}% {msg}
                      </span>
                    </div>
                  );
                })()}
                {titolari.map((t, i) => (
                  <div key={i} className="border-2 border-slate-200 rounded-lg mb-2 overflow-hidden" data-testid={`titolare-${i}`}>
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 cursor-pointer" onClick={() => setOpenTit(openTit === i ? -1 : i)}>
                      <span className="text-sm font-semibold text-slate-700 truncate">
                        {t.nome || `Soggetto ${i + 1}`}{t.diritto_reale ? ` · ${t.diritto_reale}` : ""}{t.quota_diritto ? ` · ${t.quota_diritto}%` : ""}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-slate-400">{openTit === i ? "▲" : "▼"}</span>
                        <button type="button" data-testid={`titolare-remove-${i}`} onClick={(e) => { e.stopPropagation(); removeTitolare(i); }} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                    {openTit === i && (
                      <div className="grid grid-cols-2 gap-2 p-3">
                        <div className="col-span-2">
                          <label className="text-xs font-bold text-slate-600">Cognome e nome</label>
                          <input data-testid={`tit-nome-${i}`} className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 text-sm" value={t.nome} onChange={(e) => setTit(i, "nome", e.target.value)} />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-bold text-slate-600">Codice fiscale</label>
                          <input data-testid={`tit-cf-${i}`} className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 text-sm" value={t.codice_fiscale} onChange={(e) => setTit(i, "codice_fiscale", e.target.value.toUpperCase())} />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-bold text-slate-600">Tipologia diritto</label>
                          <select data-testid={`tit-diritto-${i}`} className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 text-sm bg-white" value={t.diritto_reale} onChange={(e) => setTit(i, "diritto_reale", e.target.value)}>
                            <option value="">— Seleziona —</option>
                            {dirittiReali.map((o, k) => <option key={k} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-600">% diritto</label>
                          <input data-testid={`tit-quota-${i}`} type="number" className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 text-sm" value={t.quota_diritto} onChange={(e) => setTit(i, "quota_diritto", e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-600">N. occupanti</label>
                          <input data-testid={`tit-occ-${i}`} type="number" className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 text-sm" value={t.num_occupanti} onChange={(e) => setTit(i, "num_occupanti", e.target.value)} />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-bold text-slate-600">Tipologia possesso</label>
                          <select data-testid={`tit-possesso-${i}`} className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 text-sm bg-white" value={t.tipo_possesso} onChange={(e) => setTit(i, "tipo_possesso", e.target.value)}>
                            <option value="">— Seleziona —</option>
                            {POSSESSO_OPZIONI.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                          </select>
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-bold text-slate-600">% detrazione fiscale del soggetto</label>
                          <input data-testid={`tit-detr-${i}`} type="number" className="w-full h-10 border-2 border-slate-300 rounded-lg px-2 text-sm" value={t.percentuale_detrazione} onChange={(e) => setTit(i, "percentuale_detrazione", e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" data-testid="add-titolare" onClick={addTitolare}
                  className="mt-1 w-full text-sm font-bold text-brand border-2 border-dashed border-brand/40 rounded-lg px-3 py-2 hover:bg-brand/5 flex items-center justify-center gap-1.5">
                  <Plus className="w-4 h-4" /> altri soggetti titolari di diritti
                </button>
              </div>
            )}

            {/* Documenti (ID / Codice fiscale) — caricamento diretto durante la compilazione */}
            <div className="mt-5" data-testid="rac-documenti-section">
              <label className="block text-sm font-bold text-slate-700 mb-1 flex items-center gap-1.5">
                <Paperclip className="w-4 h-4 text-brand" /> Documenti anagrafici (opzionale)
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Se vuole, alleghi copia del <b>documento d'identità</b> e/o del <b>codice fiscale/tessera sanitaria</b>. Formati: JPG/PNG/PDF · max 20 MB.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select data-testid="rac-doc-tipo" value={docTipo} onChange={(e) => setDocTipo(e.target.value)}
                  className="h-10 border-2 border-slate-300 rounded-lg px-2 bg-white text-sm">
                  <option value="identita">Documento d'identità</option>
                  <option value="codice_fiscale">Codice fiscale / Tessera sanitaria</option>
                </select>
                <label className="inline-flex items-center gap-2 h-10 px-3 border-2 border-dashed rounded-lg cursor-pointer text-slate-500 hover:border-brand text-sm" data-testid="rac-doc-upload">
                  {uploadingDoc ? <><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</> : <><Upload className="w-4 h-4" /> Carica file</>}
                  <input type="file" accept="image/*,application/pdf" disabled={uploadingDoc} className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f); e.target.value = ""; }} />
                </label>
              </div>
              {docErr && <p className="text-xs text-red-700 mt-2" data-testid="rac-doc-err">{docErr}</p>}
              {docs.length > 0 && (
                <div className="mt-3 space-y-1">
                  {docs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2 bg-slate-50" data-testid={`rac-doc-item-${d.id}`}>
                      <div className="min-w-0 text-sm">
                        <span className="font-semibold text-slate-800 truncate">{d.filename}</span>
                        <span className="text-xs text-slate-500 ml-2">{d.tipo === "identita" ? "Documento d'identità" : d.tipo === "codice_fiscale" ? "Codice fiscale" : d.tipo}</span>
                      </div>
                      <button type="button" onClick={() => removeDoc(d.id)} className="text-red-600 hover:text-red-800 shrink-0" data-testid={`rac-doc-del-${d.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5 space-y-3 border-t pt-4">
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={atto} onChange={(e) => setAtto(e.target.checked)} className="mt-1" data-testid="rac-atto-notorio" />
                <span>{data.disclosure}</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1" data-testid="rac-privacy-consenso" />
                <span>
                  Ho letto e autorizzo il trattamento dei dati personali trasmessi ai sensi del Reg. UE 2016/679 (GDPR).{" "}
                  <button type="button" onClick={() => setShowPrivacy((s) => !s)} className="text-brand underline" data-testid="rac-privacy-toggle">
                    {showPrivacy ? "Nascondi informativa" : "Leggi l'informativa"}
                  </button>
                </span>
              </label>
              {showPrivacy && (
                <div className="text-xs text-slate-600 bg-slate-50 border rounded-lg p-3 max-h-52 overflow-y-auto space-y-2" data-testid="rac-privacy-testo">
                  {(data.privacy_informativa || []).map((sez, i) => (
                    <div key={i}>
                      <div className="font-bold text-slate-700">{Array.isArray(sez) ? sez[0] : ""}</div>
                      <div>{Array.isArray(sez) ? sez[1] : sez}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button data-testid="rac-invia-btn" disabled={!(atto && privacy) || busy} onClick={invia}
              className="mt-6 w-full h-14 rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              <ShieldCheck className="w-6 h-6" /> {busy ? "Invio…" : "Invia i dati"}
            </button>
            {!(atto && privacy) && <p className="text-xs text-slate-400 mt-2 text-center">Per inviare, accetta la dichiarazione e il consenso privacy.</p>}
          </div>
        )}

        {step === "done" && done && (
          <div className="text-center py-8" data-testid="rac-done">
            <CheckCircle2 className={`w-16 h-16 mx-auto mb-3 ${done.ok ? "text-green-600" : "text-amber-500"}`} />
            <p className="text-lg font-semibold text-slate-800">{done.message}</p>
            <p className="text-sm text-slate-500 mt-2">Può chiudere questa pagina.</p>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-6" data-testid="public-footer-studio">{status?.titolare?.ragione_sociale || "Ginius"} · Registro Anagrafe Condominiale · Link valido 15 giorni</p>
    </div>
  );
}
